import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { type AuthAction, useAuthSessionStore } from "../features/auth";
import { createRuntimeApiClient } from "../lib/api-runtime";
import { type ShellCommands } from "../lib/shell-commands";

interface UseRemoteAuthActionsOptions {
  commandsRef: MutableRefObject<ShellCommands | null>;
  runtimeApiClientRef: MutableRefObject<ReturnType<typeof createRuntimeApiClient> | null>;
}

interface UseRemoteAuthActionsResult {
  pendingAuthAction: AuthAction;
  authErrorMessage: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  resetAuthUiState: () => void;
}

export function useRemoteAuthActions({
  commandsRef,
  runtimeApiClientRef,
}: Readonly<UseRemoteAuthActionsOptions>): UseRemoteAuthActionsResult {
  const tokenSyncInFlightRef = useRef(false);
  const [pendingAuthAction, setPendingAuthAction] = useState<AuthAction>(null);
  const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(null);
  const hasAccessToken = useAuthSessionStore((state) => Boolean(state.accessToken));

  useEffect(() => {
    if (hasAccessToken) {
      setAuthErrorMessage(null);
    }
  }, [hasAccessToken]);

  useEffect(() => {
    if (!hasAccessToken) {
      return;
    }

    let cancelled = false;

    void runtimeApiClientRef.current?.getMe().then((result) => {
      if (cancelled || !result.success) {
        return;
      }

      useAuthSessionStore.getState().mergeUserInfo(result.data);
    });

    return () => {
      cancelled = true;
    };
  }, [hasAccessToken, runtimeApiClientRef]);

  const syncAuthTokenFromShell = useCallback(
    async (options?: { silentFailure?: boolean }): Promise<string | null> => {
      const silentFailure = options?.silentFailure ?? false;
      const commands = commandsRef.current;
      if (!commands) {
        if (!silentFailure) {
          setAuthErrorMessage("Shell connection not available.");
        }
        return null;
      }

      if (tokenSyncInFlightRef.current) {
        return useAuthSessionStore.getState().accessToken;
      }

      tokenSyncInFlightRef.current = true;

      try {
        const refreshedToken = await commands.requestAuthToken();
        useAuthSessionStore.getState().setAccessToken(refreshedToken);
        if (refreshedToken) {
          setAuthErrorMessage(null);
          return refreshedToken;
        }

        if (!silentFailure && useAuthSessionStore.getState().isAuthenticated) {
          setAuthErrorMessage(
            "Session issue detected. Reconnect to continue syncing.",
          );
        }
      } catch (error) {
        if (!silentFailure) {
          setAuthErrorMessage(
            error instanceof Error ? error.message : "Session refresh failed.",
          );
        }
      } finally {
        tokenSyncInFlightRef.current = false;
      }

      return null;
    },
    [commandsRef],
  );

  const signIn = useCallback(async () => {
    const commands = commandsRef.current;
    if (!commands) {
      setAuthErrorMessage("Shell connection not available.");
      return;
    }

    setAuthErrorMessage(null);
    setPendingAuthAction("sign-in");

    try {
      const authState = await commands.signIn();
      useAuthSessionStore.getState().setAuthState(authState);
      if (!authState.isAuthenticated) {
        useAuthSessionStore.getState().setAccessToken(null);
        setAuthErrorMessage("Sign-in did not complete. Please try again.");
        return;
      }

      await syncAuthTokenFromShell();
    } catch (error) {
      setAuthErrorMessage(
        error instanceof Error ? error.message : "Sign-in request failed.",
      );
    } finally {
      setPendingAuthAction(null);
    }
  }, [commandsRef, syncAuthTokenFromShell]);

  const signOut = useCallback(async () => {
    const commands = commandsRef.current;
    if (!commands) {
      setAuthErrorMessage("Shell connection not available.");
      return;
    }

    setAuthErrorMessage(null);
    setPendingAuthAction("sign-out");

    try {
      const authState = await commands.signOut();
      useAuthSessionStore.getState().setAuthState(authState);
      useAuthSessionStore.getState().setAccessToken(null);
      if (authState.isAuthenticated) {
        setAuthErrorMessage("Sign-out did not complete. Please retry.");
      }
    } catch (error) {
      setAuthErrorMessage(
        error instanceof Error ? error.message : "Sign-out request failed.",
      );
    } finally {
      setPendingAuthAction(null);
    }
  }, [commandsRef]);

  const resetAuthUiState = useCallback(() => {
    setPendingAuthAction(null);
    setAuthErrorMessage(null);
    tokenSyncInFlightRef.current = false;
  }, []);

  return {
    pendingAuthAction,
    authErrorMessage,
    signIn,
    signOut,
    resetAuthUiState,
  };
}
